/**
 * 急漲 / 急跌偵測（L1 即時層，非 LLM）。
 *
 * 判定核心移植自 us-stocks dashboard.js calcSignal ⑥「微動能加速度」+ ⑤「量爆」：
 *   - 動能：近 MOMENTUM_BARS 根的漲跌幅 momentumPct。
 *   - 方向確認：最後一根 close 對前一根的方向 lastDir。動能達門檻 **且** 最後一根同向才標 surge；
 *     動能還在但最後一根反向 → 視為「轉折」不標（治本舊版「急殺卻還卡在急漲」）。
 *   - 量爆佐證：最後一根量 vs 前幾根均量 volRatio。真急漲通常帶量；徽章爆量時加標 ⚡（佐證可信度）。
 *
 * 與時間框：粗時間框（每根 > MAX_BAR_SEC，如 30/60 分）不標 surge——
 *   「3 根」在粗框時間跨度太大、不是「即時急動」的尺度（盤中看急漲急跌走 1m / 5m）。
 *   主力 consumer（MiniKLine / 收合卡 peek）都是 1m，會正常判定。
 */

export interface SurgeCandle {
  timestamp: number; // unix seconds
  close: number;
  /** 量爆佐證用；無值（型別未帶 / 後端未回）則不做量能判定。 */
  volume?: number;
}

export interface LiveQuote {
  price: number;
  /** 對昨收的當日漲跌幅（顯示用，維持 px-bar / px-chg 既有語意）。 */
  dayPct: number;
  /** 近 MOMENTUM_BARS 根動能 %（>0 上行、<0 下行）；徽章顯示用。資料不足回 0。 */
  momentumPct: number;
  /** 最後一根量 / 前幾根均量；無 volume 資料時為 1。≥VOL_BURST 視為爆量。 */
  volRatio: number;
  surge: 'up' | 'down' | null;
}

function isTaiwanSymbol(symbol: string): boolean {
  return symbol.endsWith('.TW') || symbol.endsWith('.TWO');
}

const MOMENTUM_BARS = 3;        // us-stocks ⑥：近 3 根動能
const MAX_BAR_SEC = 8 * 60;     // 每根 > 8 分（粗時間框 30/60 分）→ 不判即時 surge
const VOL_BURST = 1.5;          // 量爆佐證門檻（us-stocks scalping 用 3x；此處當顯示佐證、放寬）

/** 最近相鄰 K 棒的時間間隔（秒）中位數；用來判斷時間框粗細。資料不足回 0。 */
function medianBarSec(candles: SurgeCandle[]): number {
  const n = candles.length;
  if (n < 2) return 0;
  const diffs: number[] = [];
  for (let i = Math.max(1, n - 6); i < n; i++) {
    const d = candles[i]!.timestamp - candles[i - 1]!.timestamp;
    if (d > 0) diffs.push(d);
  }
  if (!diffs.length) return 0;
  diffs.sort((a, b) => a - b);
  return diffs[Math.floor(diffs.length / 2)]!;
}

/** 量爆倍數：最後一根量 / 前 MOMENTUM_BARS 根均量。無 volume 資料回 1（不做量能）。 */
function volRatioOf(candles: SurgeCandle[]): number {
  const n = candles.length;
  if (n < 2) return 1;
  const lastVol = candles[n - 1]!.volume;
  if (lastVol == null || !Number.isFinite(lastVol) || lastVol <= 0) return 1;
  const base: number[] = [];
  for (let i = Math.max(0, n - 1 - MOMENTUM_BARS); i < n - 1; i++) {
    const v = candles[i]!.volume;
    if (v != null && Number.isFinite(v) && v > 0) base.push(v);
  }
  if (!base.length) return 1;
  const avgBase = base.reduce((a, b) => a + b, 0) / base.length;
  return avgBase > 0 ? lastVol / avgBase : 1;
}

/**
 * 從盤中 candles（timestamp + close + 選配 volume）+ 昨收算出即時報價與急漲 / 急跌旗標。
 * 急漲 / 急跌 = 近 MOMENTUM_BARS 根動能達門檻 **且** 最後一根同向（us-stocks 微動能加速度）。
 * 門檻台股較寬、美股較緊。粗時間框 / 資料不足不標。資料不足回 null。
 */
export function computeSurge(
  rawCandles: SurgeCandle[] | undefined,
  previousClose: number | undefined,
  symbol: string,
): LiveQuote | null {
  const candles = (rawCandles ?? []).filter(
    (c) => Number.isFinite(c.close) && Number.isFinite(c.timestamp),
  );
  if (candles.length < 2) return null;
  const n = candles.length;
  const lastClose = candles[n - 1]!.close;
  const ref =
    typeof previousClose === 'number' && Number.isFinite(previousClose) && previousClose > 0
      ? previousClose
      : candles[0]!.close;
  const dayPct = ref > 0 ? ((lastClose - ref) / ref) * 100 : 0;

  // 近 MOMENTUM_BARS 根動能 + 最後一根方向（us-stocks ⑥ 微動能加速度）。
  const cPrev = candles[n - 2]!.close;
  const cBase = candles[Math.max(0, n - 1 - MOMENTUM_BARS)]!.close;
  const momentumPct = cBase > 0 ? ((lastClose - cBase) / cBase) * 100 : 0;
  const lastDir = lastClose - cPrev;
  const volRatio = volRatioOf(candles);

  // 動能門檻：台股 0.6% / 美股 0.5%（近 3 根走這麼多 = 在急動）。
  const momTh = isTaiwanSymbol(symbol) ? 0.6 : 0.5;
  const barSec = medianBarSec(candles);

  let surge: 'up' | 'down' | null = null;
  // 細時間框才判即時 surge；動能達門檻 + 最後一根同向才算「真加速」（反向 = 轉折，不標）。
  if (barSec > 0 && barSec <= MAX_BAR_SEC) {
    if (momentumPct >= momTh && lastDir > 0) surge = 'up';
    else if (momentumPct <= -momTh && lastDir < 0) surge = 'down';
  }

  return { price: lastClose, dayPct, momentumPct, volRatio, surge };
}

/** surge 徽章要顯示的數字：近期動能 %（>0 上行、<0 下行）。 */
export function surgeMagnitude(live: LiveQuote): number {
  return live.momentumPct;
}

/** 是否伴隨量爆（最後一根量明顯放大）；徽章用來加標 ⚡ 佐證可信度。 */
export function isVolBurst(live: LiveQuote): boolean {
  return live.volRatio >= VOL_BURST;
}
